import React, { useEffect, useState, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import PageHeader from '../components/PageHeader'
import TableFilters from '../components/TableFilters'

const CATEGORIES = ['Portfolio', 'Design Examples', 'Documents', 'Templates', 'Other']

const EMPTY = { title: '', url: '', category: 'Portfolio', description: '' }

const FILTERS = [
  { key: 'search', label: 'Search', type: 'text', fields: ['title', 'description'] },
  { key: 'category', label: 'Category', type: 'select', options: CATEGORIES },
]

function catBadge(c) {
  const map = {
    'Portfolio': 'badge badge-blue',
    'Design Examples': 'badge badge-purple',
    'Documents': 'badge badge-green',
    'Templates': 'badge badge-amber',
    'Other': 'badge badge-gray',
  }
  return map[c] || 'badge badge-gray'
}

function fmtDate(ts) {
  if (!ts) return '—'
  return new Date(ts).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
}

function cleanUrl(url) {
  if (!url) return url
  if (!/^https?:\/\//i.test(url)) return 'https://' + url
  return url
}

export default function Resources() {
  const [resources, setResources] = useState([])
  const [filtered, setFiltered] = useState([])
  const [modal, setModal] = useState(false)
  const [form, setForm] = useState(EMPTY)
  const [editing, setEditing] = useState(null)
  const [saving, setSaving] = useState(false)

  const handleFiltered = useCallback(d => setFiltered(d), [])

  useEffect(() => {
    supabase.from('resources').select('*').order('created_at', { ascending: false })
      .then(({ data }) => { if (data?.length) setResources(data) })
  }, [])

  function openAdd() { setForm(EMPTY); setEditing(null); setModal(true) }
  function openEdit(r) { setForm(r); setEditing(r.id); setModal(true) }

  async function save() {
    setSaving(true)
    const payload = { ...form, url: cleanUrl(form.url) }
    if (editing) {
      const { data } = await supabase.from('resources').update(payload).eq('id', editing).select()
      setResources(prev => prev.map(r => r.id === editing ? (data?.[0] || { ...r, ...payload }) : r))
    } else {
      const { data } = await supabase.from('resources').insert([payload]).select()
      setResources(prev => [data?.[0] || { ...payload, id: Date.now() }, ...prev])
    }
    setSaving(false)
    setModal(false)
  }

  async function remove(id) {
    if (!window.confirm('Delete this resource?')) return
    await supabase.from('resources').delete().eq('id', id)
    setResources(prev => prev.filter(r => r.id !== id))
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      <PageHeader
        title="Resources"
        subtitle={`${resources.length} shared resource${resources.length !== 1 ? 's' : ''}`}
        action={<button className="btn btn-primary" onClick={openAdd}>+ Add resource</button>}
      />
      <div style={{ padding: 28, flex: 1 }}>
        <TableFilters filters={FILTERS} data={resources} onFiltered={handleFiltered} />
        <div className="card" style={{ overflowX: 'auto' }}>
          <table style={{ minWidth: 700 }}>
            <thead>
              <tr>
                <th>Title</th>
                <th>Category</th>
                <th>Description</th>
                <th>Added</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => (
                <tr key={r.id}>
                  <td style={{ fontWeight: 500 }}>
                    <a href={cleanUrl(r.url)} target="_blank" rel="noopener noreferrer"
                      style={{ color: 'var(--accent)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6 }}>
                      {r.title}
                      <svg width="11" height="11" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ opacity: 0.6, flexShrink: 0 }}>
                        <path d="M5 2H2a1 1 0 00-1 1v7a1 1 0 001 1h7a1 1 0 001-1V7M7 1h4m0 0v4m0-4L5 7"/>
                      </svg>
                    </a>
                  </td>
                  <td><span className={catBadge(r.category)}>{r.category}</span></td>
                  <td style={{ color: 'var(--text2)', fontSize: 13, maxWidth: 340 }}>{r.description || '—'}</td>
                  <td style={{ color: 'var(--text3)', fontSize: 12, whiteSpace: 'nowrap' }}>{fmtDate(r.created_at)}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button className="btn" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => openEdit(r)}>Edit</button>
                      <button className="btn btn-danger" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => remove(r.id)}>Del</button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--text3)', padding: 40 }}>
                  No resources yet — add links to portfolios, docs, and design files
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div className="modal" style={{ maxWidth: 480 }}>
            <div className="modal-header">
              <h3>{editing ? 'Edit resource' : 'Add resource'}</h3>
              <button className="modal-close" onClick={() => setModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-field">
                <label>Title *</label>
                <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="e.g. Company Portfolio 2025" />
              </div>
              <div className="form-field">
                <label>URL / Link *</label>
                <input value={form.url} onChange={e => setForm({ ...form, url: e.target.value })} placeholder="https://drive.google.com/..." />
              </div>
              <div className="form-field">
                <label>Category</label>
                <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
                  {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div className="form-field">
                <label>Description</label>
                <textarea rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                  placeholder="What is this? Who should use it?" style={{ resize: 'vertical' }} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={() => setModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving || !form.title || !form.url}>
                {saving ? 'Saving…' : editing ? 'Save changes' : 'Add resource'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
